import { addQuoteNoteInputSchema } from '@repo/api-contracts';
import { useState } from 'react';
import { useInvalidateQuotes } from '@/entities/quote';
import { trpc } from '@/shared/api';
import { getErrorMessage } from '@/shared/lib';

export interface AddQuoteNoteInput {
  readonly quoteRequestId: string;
  readonly body: string;
}

export interface UseAddQuoteNoteResult {
  /** 성공하면 true. 검증 실패·서버 거절이면 false. */
  readonly addNote: (input: AddQuoteNoteInput) => Promise<boolean>;
  readonly isPending: boolean;
  readonly fieldError: string | null;
  readonly formError: string | null;
}

const BODY_MESSAGE = '메모를 1~2,000자로 입력해 주세요.';
const FORM_ERROR_FALLBACK = '메모를 남기지 못했습니다.';

/** 내부 메모 추가(quoteManagement.addNote, 견적 설계 §9-6) — 고객에게 보이지 않는다. 성공하면 상세를 다시 불러온다. */
export const useAddQuoteNote = (): UseAddQuoteNoteResult => {
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const invalidate = useInvalidateQuotes();
  const mutation = trpc.quoteManagement.addNote.useMutation();

  const addNote = async (input: AddQuoteNoteInput): Promise<boolean> => {
    setFieldError(null);
    setFormError(null);

    const parsed = addQuoteNoteInputSchema.safeParse(input);

    if (!parsed.success) {
      setFieldError(BODY_MESSAGE);
      return false;
    }

    try {
      await mutation.mutateAsync(parsed.data);
      await invalidate.detail(input.quoteRequestId);
      return true;
    } catch (cause) {
      setFormError(getErrorMessage(cause, FORM_ERROR_FALLBACK));
      return false;
    }
  };

  return { addNote, isPending: mutation.isPending, fieldError, formError };
};
